/**
 * The last logged-in screen the user visited, so the app root can bring them
 * back to it (N-281, Lead review). Kept in localStorage, so it survives a
 * browser restart. Cleared on logout (clearViewState), so the next login
 * opens Farm View, the default view.
 *
 * Only base paths from viewPaths.RESTORABLE_PATHS are stored, never query
 * params, so a one-off field link is not replayed on the next visit.
 */

const {
  getItem,
  setItem,
  removeItem,
} = require('@managers/localStorageManager.jsx');
const { getPathToRemember } = require('./viewPaths');

const LAST_VISITED_PATH_KEY = 'LAST_VISITED_PATH';
// The pre-N-281 Farm/Field preference. Read only, as a fallback for users who
// picked Field View before the last screen was stored. Never written.
const LEGACY_VIEW_MODE_KEY = 'irrigation_monitoring_view_mode';

const readKey = (key) => {
  try {
    return getItem(key, null);
  } catch (error) {
    // Blocked site data: behave as if nothing was stored.
    return null;
  }
};

const readLastVisitedPath = () => readKey(LAST_VISITED_PATH_KEY);

const readLegacyViewMode = () => readKey(LEGACY_VIEW_MODE_KEY);

/**
 * Stores the screen for `pathname` when it is a logged-in screen and differs
 * from the stored one. Public pages and the root are ignored.
 *
 * @param {string} pathname
 */
const rememberPath = (pathname) => {
  const path = getPathToRemember(pathname);
  if (!path || path === readLastVisitedPath()) return;
  try {
    setItem(LAST_VISITED_PATH_KEY, path);
  } catch (error) {
    // Quota or blocked write: the root falls back to Farm View next time.
  }
};

/**
 * Forgets the stored view state (last screen + the old Farm/Field key), so the
 * next login lands on Farm View. Called by Main.jsx on logout.
 */
const clearViewState = () => {
  [LAST_VISITED_PATH_KEY, LEGACY_VIEW_MODE_KEY].forEach((key) => {
    try {
      removeItem(key);
    } catch (error) {
      // Blocked site data: nothing was stored either.
    }
  });
};

module.exports = {
  LAST_VISITED_PATH_KEY,
  clearViewState,
  readLastVisitedPath,
  readLegacyViewMode,
  rememberPath,
};
